import { OpenScoreboardData, ParticipantId } from '../api/apiTypes'
import { ScoreboardParticipant } from '../utils/types'

export type RoundScores = Record<string, string>

const normalizeScore = (value: unknown): number => {
  const score = Number(value)
  return Number.isFinite(score) ? score : 0
}

const normalizeScores = (scores: unknown): number[] => {
  if (!Array.isArray(scores)) return []
  return scores.map(normalizeScore)
}

class OpenScoreboardResult {
  private scoreboard_id: number
  private participants: ScoreboardParticipant[]
  private resultData: OpenScoreboardData
  private scoresData: Record<ParticipantId, number[]>
  private participantIds: string[]
  private totalRoundWins: Record<ParticipantId, number>
  private totalPoints: Record<ParticipantId, number>

  constructor(participants: ScoreboardParticipant[], resultData: OpenScoreboardData) {
    this.scoreboard_id = resultData.id
    this.participants = participants
    this.participantIds = participants.map(participant => String(participant.id))
    this.resultData = resultData
    this.scoresData = Object.entries(resultData.scores_data || {}).reduce((acc, [participantId, scores]) => {
      acc[String(participantId)] = normalizeScores(scores)
      return acc
    }, {} as Record<ParticipantId, number[]>)
    this.totalRoundWins = {}
    this.totalPoints = {}
  }

  public getScoreboardId() {
    return this.scoreboard_id
  }

  public getParticipants() {
    return this.participants
  }

  public getResultData() {
    return {
      ...this.resultData,
      scores_data: this.scoresData,
    }
  }

  public getTotalRoundWins() {
    return this.totalRoundWins
  }

  public getRoundScores(roundNumber: number): RoundScores | undefined {
    return Object.entries(this.scoresData).reduce((acc, [participantId, scores]) => {
      acc[String(participantId)] = String(scores[roundNumber])
      return acc
    }, {} as RoundScores)
  }

  public getTotalPoints() {
    return this.totalPoints
  }

  public addNewRoundOfScores(scores: Record<ParticipantId, number>) {
    Object.entries(scores).forEach(([participantId, score]) => {
      this.scoresData[participantId] = [...(this.scoresData[participantId] || []), score]
    })
  }

  public deleteRoundOfScores(roundNumber: number) {
    Object.entries(this.scoresData).forEach(([participantId, scores]) => {
      this.scoresData[participantId] = scores.filter((_, i) => i !== roundNumber)
    })
  }

  public updateRoundOfScores(roundNumber: number, scores: Record<ParticipantId, number>) {
    Object.entries(scores).forEach(([participantId, score]) => {
      const scores = [...this.scoresData[participantId]]
      this.scoresData[participantId] = scores.map((_, i) => i === roundNumber ? score : scores[i])
    })
  }

  public deduceWinner() {
    this.processResult()
    return this.getWinner()
  }

  private processResult() {
    let currentWinner = null
    if (!this.participantIds.length) return currentWinner
    
    const noOfRounds = this.scoresData[this.participantIds[0]]?.length
    if (!noOfRounds) return currentWinner

    this.resetTotals()

    for (let i = 0; i < noOfRounds; i++) {
      const roundPoints: Record<ParticipantId, number> = {}
      this.participantIds.forEach(participantId => {
        roundPoints[participantId] = this.scoresData[participantId]?.[i] ?? 0
      })

      const roundWinner = this.getRoundWinner(roundPoints)
      if (roundWinner) {
        this.totalRoundWins[roundWinner] = (this.totalRoundWins[roundWinner] || 0) + 1
      }
      this.totalPoints = this.mergePoints(this.totalPoints, roundPoints)
    }
  }

  private resetTotals() {
    this.totalRoundWins = {}
    this.totalPoints = {}
  }

  private getWinnerByRoundWins(): [null | ParticipantId, boolean] {
    const entries = Object.entries(this.totalRoundWins)
    if (!entries.length) return [null, true]

    const maxRoundWins = Math.max(...entries.map(([, roundWins]) => roundWins))
    const topParticipants = entries.filter(([, roundWins]) => roundWins === maxRoundWins)
    const roundTie = topParticipants.length > 1
    const winner = roundTie || !topParticipants.length ? null : topParticipants[0][0]

    return [winner, roundTie]
  }

  private getWinnerByPoints(): [null | ParticipantId, boolean] {
    const entries = Object.entries(this.totalPoints)
    if (!entries.length) return [null, true]

    const maxPoints = Math.max(...entries.map(([, points]) => points))
    const topParticipants = entries.filter(([, points]) => points === maxPoints)
    const pointTie = topParticipants.length > 1
    const winner = pointTie || !topParticipants.length ? null : topParticipants[0][0]

    return [winner, pointTie]
  }

  private getWinner() {
    const [roundWinner, roundTie] = this.getWinnerByRoundWins()
    if (!roundTie) return roundWinner

    const [pointWinner] = this.getWinnerByPoints()
    return pointWinner
  }

  private getRoundWinner(roundPoints: Record<ParticipantId, number>) {
    let winner: null | ParticipantId = null
    Object.entries(roundPoints).forEach(([participantId, score]) => {
      if (!winner || score > roundPoints[winner]) {
        winner = participantId
      }
    })

    return winner
  }

  private mergePoints(point1: Record<ParticipantId, number>, point2: Record<ParticipantId, number>): Record<ParticipantId, number> {
    const mergedPoints: Record<ParticipantId, number> = {}
    Object.entries(point1).forEach(([participantId, score]) => {
      mergedPoints[participantId] = (mergedPoints[participantId] || 0) + score
    })
    Object.entries(point2).forEach(([participantId, score]) => {
      mergedPoints[participantId] = (mergedPoints[participantId] || 0) + score
    })
    return mergedPoints
  }
}

export default OpenScoreboardResult
